import { useSyncExternalStore } from "react";

const MINUTE = 60_000;
const listeners = new Set<() => void>();
let currentDate = new Date();
let timeout: ReturnType<typeof setTimeout> | undefined;

function getSnapshot(): Date {
  // An inactive store can be older than the current minute when a route first mounts.
  if (listeners.size === 0 && Math.floor(currentDate.getTime() / MINUTE) !== Math.floor(Date.now() / MINUTE)) {
    currentDate = new Date();
  }

  return currentDate;
}

function refresh(): void {
  currentDate = new Date();
  for (const listener of listeners) {
    listener();
  }
}

function scheduleRefresh(): void {
  // Align with minute boundaries so midnight and minute-based deadlines update on time.
  timeout = setTimeout(
    () => {
      if (!document.hidden) {
        refresh();
      }
      if (listeners.size > 0) {
        scheduleRefresh();
      }
    },
    MINUTE - (Date.now() % MINUTE),
  );
}

function handleFocus(): void {
  if (!document.hidden) {
    refresh();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    // The browser clock is an external store. Share one timer and refresh after backgrounding.
    refresh();
    scheduleRefresh();
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleFocus);
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      clearTimeout(timeout);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleFocus);
    }
  };
}

/** Current browser date, refreshed each minute and when the tab becomes active again. */
export function useCurrentDate(): Date {
  return useSyncExternalStore(subscribe, getSnapshot);
}
